// src/components/inventory/PurchaseOrderManager.js
//
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • FlowSense procurement AI: health score + findings (late deliveries, approvals waiting,
//     price increases vs earlier orders, duplicates, failing suppliers, learned lead times) with
//     actions, "what to order" grouped by supplier (inventory forecast minus what is already on
//     order) → Raise PO in one click, AI bar (explain, Ministry of Works, Ask FlowSense).
//   • Wizard checks every line live: already on an open PO, price vs what was paid before, stock
//     and days of cover after delivery, FlowSense suggested quantity; supplier grade + expected
//     delivery date from the real lead time; budget votes used before; FlowSense memo when the AI
//     engine is offline. Smart bundles keep the other suppliers' drafts and open them next.
//   • Receive goods (all / part) from the list; print; cancel; the full PO detail (signatures,
//     timeline, receiving) opens from a row. Buttons follow the user's role.
//   • Live: list, analytics and AI refresh on WebSocket purchase_order_update / inventory_update.
//   • Fixes: the wizard steps, line editor, rows and modal were components declared inside the
//     render — every keystroke remounted them (inputs lost focus, menus closed); the search
//     debounce reset the page to 0 whenever any page loaded (Next did nothing); errors were
//     swallowed (empty catch) — they now show the server's reason; "Total Spend (QTD)" showed the
//     all-time total; Partly received status added.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Flex, Text, Button, HStack, Icon, useColorModeValue, SimpleGrid,
  Stat, StatLabel, StatNumber, StatHelpText, StatArrow, Badge, Table,
  Thead, Tbody, Tr, Th, Td, IconButton, Input, InputGroup, InputLeftElement,
  Select, Textarea, NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  Menu, MenuButton, MenuList, MenuItem, Tooltip, Progress, VStack,
  useToast, Divider, Tag, TagLabel, TagLeftIcon, Avatar, AvatarGroup,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
  useDisclosure, FormControl, FormLabel, FormErrorMessage,
  Stepper, Step, StepIndicator, StepStatus, StepIcon, StepNumber,
  StepTitle, StepSeparator, useSteps, Spinner, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, Skeleton, SkeletonText,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  InputRightElement, Alert, AlertIcon, AlertDescription,
} from '@chakra-ui/react';
import {
  MdReceipt, MdAdd, MdSearch, MdFilterList, MdMoreVert, MdCheckCircle,
  MdPendingActions, MdLocalShipping, MdErrorOutline, MdAttachMoney,
  MdAccountBalanceWallet, MdDescription, MdHistory, MdRefresh, MdDelete,
  MdSend, MdThumbUp, MdThumbDown, MdClose, MdKeyboardArrowDown,
  MdInventory, MdBusiness, MdDateRange, MdPrint, MdOpenInNew,
  // UPDATE: MdEmail added for Approve & Email action and STATUS_CONFIG
  MdEmail,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import purchaseOrderService from '../../services/purchaseOrderService';
// --- UPDATE (Procurement MOW+ v1)
import { MdAutoAwesome, MdWarning, MdArrowForward, MdDownload, MdCancel } from 'react-icons/md'; // MdInventory is already imported above
import { poErrorMessage } from '../../services/purchaseOrderService';
import aiService from '../../services/aiService';
import inventoryIntelApi from '../../services/inventoryIntelApi';
import { getUserInfo } from '../../utils/auth';
import { hasRole } from '../../config/roles';
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import ReceiveGoodsModal from './ReceiveGoodsModal';
import { answerProcurementLocally, buildProcurementContext, explainProcurementForMinistry, localJustification, poPrintHtml, gradeMeta, usd, PROCUREMENT_QUESTIONS } from './procurementAiIntel';
const LEVEL_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', info: 'blue' };
// --- END UPDATE

const MotionBox  = motion(Box);
const MotionTr   = motion(Tr);

// ─── Wizard steps ────────────────────────────────────────────
const WIZARD_STEPS = [
  { title: 'Vendor',    description: 'Select supplier' },
  { title: 'Items',     description: 'Add line items' },
  { title: 'Details',   description: 'Budget & notes' },
  { title: 'Review',    description: 'Confirm & submit' },
];

// ─── Status config ────────────────────────────────────────────
const STATUS_CONFIG = {
  Pending:   { color: 'orange', icon: MdPendingActions,  label: 'Pending Approval' },
  // UPDATE: Ordered now uses MdEmail icon and 'Ordered & Emailed' label
  Ordered:   { color: 'blue',   icon: MdEmail,           label: 'Ordered & Emailed' },
  Shipped:   { color: 'cyan',   icon: MdLocalShipping,   label: 'Shipped' },
  Received:  { color: 'green',  icon: MdCheckCircle,     label: 'Received' },
  Cancelled: { color: 'red',    icon: MdErrorOutline,    label: 'Cancelled' },
  Partial:   { color: 'purple', icon: MdInventory,       label: 'Partly received' }, // --- UPDATE (Procurement MOW+ v1)
};

// ─── Empty form state ─────────────────────────────────────────
const EMPTY_FORM = {
  supplier_id:            '',
  order_date:             new Date().toISOString().slice(0, 10),
  expected_delivery_date: '',
  budget_code:            '',
  priority:               'medium',
  notes:                  '',
};

// ─── Empty line item ──────────────────────────────────────────
const EMPTY_ITEM = { item_id: '', quantity: 1, quoted_price: '' };

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const PurchaseOrderManager = ({ aiDraftItems: aiDraftItemsProp, clearAiDraft, presetSupplierId = null, clearPresetSupplier = null, command = null, legacyDetail = false }) => { // --- UPDATE: + presetSupplierId, command, legacyDetail

  // ── Design tokens ─────────────────────────────────────────
  const bg          = useColorModeValue('white', 'gray.800');
  const border      = useColorModeValue('gray.100', 'gray.700');
  const secondary   = useColorModeValue('gray.500', 'gray.400');
  const headerBg    = useColorModeValue('gray.50', 'whiteAlpha.50');
  const hoverBg     = useColorModeValue('blue.50', 'whiteAlpha.50');
  const subtleBg    = useColorModeValue('gray.50', 'gray.700');
  // --- UPDATE (Procurement MOW+ v1)
  const totalBg     = useColorModeValue('blue.50', 'rgba(49,130,206,0.15)');
  const totalText   = useColorModeValue('blue.700', 'blue.200');
  const tile        = useColorModeValue('white', 'gray.800');
  const warnBg      = useColorModeValue('orange.50', 'rgba(221,107,32,0.15)');
  // --- END UPDATE

  const toast      = useToast(); 
  const cancelRef  = useRef();

  // ── Drawer / Modal disclosures ─────────────────────────────
  const drawerDisc  = useDisclosure();   // New PO drawer
  const detailDisc  = useDisclosure();   // PO detail modal
  const rejectDisc  = useDisclosure();   // Reject confirm dialog

  // ── Page state ─────────────────────────────────────────────
  const [pos,         setPOs]        = useState([]);
  const [analytics,   setAnalytics]  = useState(null);
  const [suppliers,   setSuppliers]  = useState([]);
  const [invItems,    setInvItems]   = useState([]);
  const [invSearch,   setInvSearch]  = useState('');
  const [selectedPO,  setSelectedPO] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [total,       setTotal]      = useState(0);

  const [loading,         setLoading]         = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [submitting,      setSubmitting]      = useState(false);
  const [actionLoading,   setActionLoading]   = useState({});

  // ── Filter state ───────────────────────────────────────────
  const [searchTerm,    setSearchTerm]    = useState('');
  const [filterStatus,  setFilterStatus]  = useState('All');
  const [page,          setPage]          = useState(0);
  const LIMIT = 20;

  // ── New PO wizard ──────────────────────────────────────────
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [lineItems,   setLineItems]   = useState([{ ...EMPTY_ITEM }]);
  const [formErrors,  setFormErrors]  = useState({});

  const { activeStep, setActiveStep } = useSteps({ index: 0, count: WIZARD_STEPS.length });

  // --- UPDATE (Procurement MOW+ v1): intel, draft checks, queues, roles
  const me = useMemo(() => getUserInfo() || {}, []);
  const canRaise = hasRole(me, '@office');
  const canOverride = hasRole(me, 'admin', 'manager');
  const [intel, setIntel] = useState(null);
  const [intelError, setIntelError] = useState(null);
  const [invInsights, setInvInsights] = useState(null);
  const [check, setCheck] = useState(null);
  const [draftQueue, setDraftQueue] = useState([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [memoBusy, setMemoBusy] = useState(false);
  const [showAllFindings, setShowAllFindings] = useState(false);
  const [resolvedDraft, setResolvedDraft] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null);
  const aiDraftItems = pendingDraft || aiDraftItemsProp; // --- UPDATE: resolved draft replaces the prop once suppliers are known
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────
  const loadPOs = useCallback(async (quiet = false) => { // --- UPDATE: + quiet (live refresh)
    if (quiet !== true) setLoading(true);
    try {
      const result = await purchaseOrderService.getPaginatedPOs({
        status:  filterStatus !== 'All' ? filterStatus : undefined,
        search:  debouncedSearch || undefined, // --- UPDATE: debounced (was searchTerm — a request per keystroke)
        limit:   LIMIT,
        offset:  page * LIMIT,
      });
      if (!mountedRef.current) return; // --- UPDATE
      setPOs(Array.isArray(result.data) ? result.data : []);
      setTotal(result.total ?? 0);
    } catch (err) {
      // UPDATE: Silent catch — analytics failure shouldn't block the table
      if (quiet !== true) setPOs([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filterStatus, debouncedSearch, page]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const data = await purchaseOrderService.getAnalytics();
      setAnalytics(data);
    } catch {
      // Non-blocking – analytics failure shouldn't block the table
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadSuppliers = useCallback(async () => {
    // UPDATE: Silent catch — no toast on supplier load failure
    try {
      setSuppliers(await purchaseOrderService.getSuppliers());
    } catch {}
  }, []);

  const loadInventoryItems = useCallback(async () => {
    // UPDATE: Silent catch
    try {
      setInvItems(await purchaseOrderService.getInventoryItems(invSearch));
    } catch {}
  }, [invSearch]);

  // Initial loads
  useEffect(() => { loadPOs(); },        [loadPOs]);
  useEffect(() => { loadAnalytics(); },  [loadAnalytics]);
  useEffect(() => { loadSuppliers(); },  [loadSuppliers]);
  useEffect(() => { loadInventoryItems(); }, [loadInventoryItems]);

  // Debounce search
  // --- UPDATE: only the search text is debounced; it used to depend on loadPOs, so loading any page
  //     reset the page to 0 (Next / Prev did nothing) and every page loaded twice
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(searchTerm.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);
  // --- END UPDATE

  // --- UPDATE (Procurement MOW+ v1): FlowSense procurement intel, inventory insights, live, draft check
  const loadIntel = useCallback(async (refresh = false) => {
    try { const o = await purchaseOrderService.getIntel({ refresh }); if (mountedRef.current) { setIntel(o); setIntelError(null); live.markUpdated(); } }
    catch (e) { if (mountedRef.current) setIntelError(e?.response?.status === 404 ? 'FlowSense procurement intelligence is not on this server yet — deploy the updated purchaseOrderRoutes.js.' : poErrorMessage(e)); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadInsights = useCallback(async () => {
    try { const o = await inventoryIntelApi.overview(); if (mountedRef.current) setInvInsights(o.items || []); } catch (_) { /* optional */ }
  }, []);
  useEffect(() => { loadIntel(); loadInsights(); }, [loadIntel, loadInsights]);
  const live = useInventoryLive((e) => { loadPOs(true); loadAnalytics(); loadIntel(); if (e.type !== 'purchase_order_update') loadInsights(); }, { events: ['purchase_order_update', 'inventory_update'] });
  const refreshAll = () => { loadPOs(true); loadAnalytics(); loadIntel(true); loadInsights(); };
  const overdueIds = useMemo(() => new Map((intel?.overdue || []).map((o) => [String(o.po_id), o])), [intel]);
  const supplierScore = useMemo(() => new Map((intel?.suppliers || []).map((s) => [String(s.supplier_id), s])), [intel]);

  // live check of the draft (duplicates, price, stock, lead time)
  useEffect(() => {
    if (!drawerDisc.isOpen) return undefined;
    const items = lineItems.filter((l) => l.item_id).map((l) => ({ item_id: Number(l.item_id), quantity: Number(l.quantity) || 0, unit_price: l.quoted_price === '' ? null : Number(l.quoted_price) }));
    if (!formData.supplier_id && !items.length) { setCheck(null); return undefined; }
    const t = setTimeout(async () => {
      try { const c = await purchaseOrderService.checkDraft({ supplier_id: formData.supplier_id || null, items }); if (mountedRef.current) setCheck(c); } catch (_) { if (mountedRef.current) setCheck(null); }
    }, 450);
    return () => clearTimeout(t);
  }, [drawerDisc.isOpen, formData.supplier_id, lineItems]);
  const checkByItem = useMemo(() => new Map((check?.lines || []).map((l) => [String(l.item_id), l])), [check]);
  // expected delivery left empty → the date from the supplier's real lead time (still editable)
  useEffect(() => {
    if (drawerDisc.isOpen && check?.suggested_delivery_date && !formData.expected_delivery_date) setFormData((p) => (p.expected_delivery_date ? p : { ...p, expected_delivery_date: check.suggested_delivery_date }));
  }, [check?.suggested_delivery_date]); // eslint-disable-line react-hooks/exhaustive-deps

  // preset supplier (Suppliers tab → Raise PO)
  useEffect(() => {
    if (!presetSupplierId) return;
    resetForm(); setFormData((p) => ({ ...p, supplier_id: String(presetSupplierId) })); drawerDisc.onOpen();
    clearPresetSupplier?.();
  }, [presetSupplierId]); // eslint-disable-line react-hooks/exhaustive-deps
  // quick actions from the page
  useEffect(() => {
    if (!command?.type) return;
    if (command.type === 'raisePO' && canRaise) { resetForm(); drawerDisc.onOpen(); }
    else if (command.type === 'exportPOs') handleExportCsv();
    else if (command.type === 'refreshPOs') refreshAll();
  }, [command]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────
  // UPDATE: PHASE 3 — SMART PO BUNDLING
  // Groups AI draft items by supplier, loads the primary supplier's
  // items into the form, and shows a smart toast for multi-supplier drafts.
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (aiDraftItems && aiDraftItems.length > 0) {
      // --- UPDATE (Procurement MOW+ v1): items without a supplier get the item's supplier from the catalogue first
      if (aiDraftItems.some((i) => !i.supplier_id) && !aiDraftItems.__resolved) {
        (async () => {
          let cat = invItems;
          if (!cat?.length) { try { cat = await purchaseOrderService.getInventoryItems(''); } catch (_) { cat = []; } }
          const sup = new Map((cat || []).map((x) => [String(x.id), x.supplier_id]));
          const resolved = aiDraftItems.map((i) => ({ ...i, supplier_id: i.supplier_id || sup.get(String(i.item_id)) || null }));
          resolved.__resolved = true;
          setResolvedDraft(resolved);
        })();
        return;
      }
      // --- END UPDATE
      // Group items by supplier to avoid manual sorting
      const groupedBySupplier = aiDraftItems.reduce((acc, item) => {
        const sId = item.supplier_id || 'unassigned';
        if (!acc[sId]) acc[sId] = [];
        acc[sId].push(item);
        return acc;
      }, {});

      const supplierIds = Object.keys(groupedBySupplier);
      const primarySupplier = supplierIds[0];

      const mappedItems = groupedBySupplier[primarySupplier].map(item => ({
        item_id: item.item_id,
        quantity: item.quantity,
        quoted_price: item.unit_price || 0
      }));
      // --- UPDATE: keep the other suppliers' drafts — they open after this one is submitted
      setDraftQueue(supplierIds.slice(1).map((sid) => ({ supplier_id: sid === 'unassigned' ? '' : sid, items: groupedBySupplier[sid].map((item) => ({ item_id: item.item_id, quantity: item.quantity, quoted_price: item.unit_price || 0 })) })));
      // --- END UPDATE

      setFormData(prev => ({
        ...prev,
        supplier_id: primarySupplier === 'unassigned' ? '' : primarySupplier,
        notes: `--- AI Predictive Procurement Auto-Draft ---\nGenerated based on dynamic stock velocity and run-out projections.`
      }));
      setLineItems(mappedItems);

      drawerDisc.onOpen();
      setActiveStep(1); // Skip straight to item review step

      toast({
        title: supplierIds.length > 1 ? 'Smart Bundle Created' : 'AI Draft Loaded',
        description: supplierIds.length > 1
          ? `Loaded ${mappedItems.length} items for the first supplier. ${supplierIds.length - 1} more supplier draft(s) open after you submit this one.` // --- UPDATE: they really are queued now
          : 'Review line items and assign budget code.',
        status: 'info',
        duration: 6000,
        position: 'top-right'
      });

      if (clearAiDraft) clearAiDraft();
      if (pendingDraft) setPendingDraft(null); // --- UPDATE
    }
  }, [aiDraftItems, clearAiDraft, drawerDisc, setActiveStep, toast]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- UPDATE: run the bundling again once the suppliers are resolved
  useEffect(() => {
    if (!resolvedDraft) return;
    const r = resolvedDraft; setResolvedDraft(null);
    if (clearAiDraft) clearAiDraft();
    setPendingDraft(r);
  }, [resolvedDraft]); // eslint-disable-line react-hooks/exhaustive-deps
  // --- END UPDATE

  // ─────────────────────────────────────────────────────────
  // FORM LOGIC
  // ─────────────────────────────────────────────────────────
  const updateField = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    setFormErrors(p => ({ ...p, [field]: undefined }));
  };

  const addLineItem = () => setLineItems(p => [...p, { ...EMPTY_ITEM }]);

  const removeLineItem = (idx) =>
    setLineItems(p => p.filter((_, i) => i !== idx));

  const updateLineItem = (idx, field, value) => {
    setLineItems(p => {
      const next = [...p];
      next[idx] = { ...next[idx], [field]: value };
      // Auto-fill price from catalogue
      if (field === 'item_id') {
        const inv = invItems.find(i => String(i.id) === String(value));
        if (inv) next[idx].quoted_price = inv.unit_price || '';
      }
      return next;
    });
  };

  const lineItemTotal = useMemo(() =>
    lineItems.reduce((sum, li) =>
      sum + (parseFloat(li.quoted_price) || 0) * (parseInt(li.quantity, 10) || 0), 0),
    [lineItems]
  );

  // ─────────────────────────────────────────────────────────
  // VALIDATION
  // ─────────────────────────────────────────────────────────
  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!formData.supplier_id) errs.supplier_id = 'Please select a supplier';
      if (!formData.order_date)  errs.order_date  = 'Order date is required';
    }
    if (step === 1) {
      lineItems.forEach((li, i) => {
        if (!li.item_id)                              errs[`item_${i}_id`]    = 'Select an item';
        if (!li.quantity || li.quantity < 1)          errs[`item_${i}_qty`]   = 'Qty ≥ 1';
        if (!li.quoted_price || li.quoted_price <= 0) errs[`item_${i}_price`] = 'Enter price';
      });
    }
    if (step === 2) {
      if (!formData.budget_code) errs.budget_code = 'Budget code is required';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep(s => s + 1);
  };
  const handleBack = () => setActiveStep(s => s - 1);

  // ─────────────────────────────────────────────────────────
  // SUBMIT NEW PO
  // ─────────────────────────────────────────────────────────
  const handleSubmitPO = async () => {
    if (!validateStep(2)) return;
    setSubmitting(true);
    try {
      const items = lineItems.map(li => ({
        item_id:      parseInt(li.item_id, 10),
        quantity:     parseInt(li.quantity, 10),
        quoted_price: parseFloat(li.quoted_price),
        unit_price:   parseFloat(li.quoted_price),
      }));
      const newPO = await purchaseOrderService.createPO(formData, items);
      setPOs(p => [newPO, ...(Array.isArray(p) ? p : []).filter((x) => x.id !== newPO.id)]); // --- UPDATE: no duplicate row when the live refresh arrived first
      await loadAnalytics();
      loadIntel(true); // --- UPDATE
      drawerDisc.onClose();
      resetForm();
      toast({ title: `${newPO.po_number || `PO #${newPO.id}`} created`, description: 'Pending approval — next signature: Warehouse Clerk.', status: 'success', duration: 6000, position: 'top-right' });
      // --- UPDATE: next queued supplier draft
      if (draftQueue.length) {
        const [next, ...rest] = draftQueue;
        setDraftQueue(rest);
        setTimeout(() => {
          setFormData(() => ({ ...EMPTY_FORM, supplier_id: next.supplier_id, notes: '--- AI Predictive Procurement Auto-Draft ---\nGenerated based on dynamic stock velocity and run-out projections.' }));
          setLineItems(next.items); setActiveStep(1); drawerDisc.onOpen();
          toast({ title: 'Next supplier draft', description: `${next.items.length} item(s)${rest.length ? ` · ${rest.length} more after this` : ''}`, status: 'info', duration: 4000, position: 'top-right' });
        }, 400);
      }
      // --- END UPDATE
    } catch (err) {
      toast({ title: 'Failed to create PO', description: poErrorMessage(err), status: 'error', duration: 6000, position: 'top-right' }); // --- UPDATE: server reason
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setLineItems([{ ...EMPTY_ITEM }]);
    setFormErrors({});
    setActiveStep(0);
  };

  // ─────────────────────────────────────────────────────────
  // ACTIONS: Approve & Email / Reject / Status update
  // ─────────────────────────────────────────────────────────
  const setActionBusy = (id, busy) =>
    setActionLoading(p => ({ ...p, [id]: busy }));

  // UPDATE: PHASE 3 — 1-CLICK APPROVE & EMAIL SUPPLIER
  // Marks the PO as Approved in DB, triggers backend email service (JIT
  // Dispatch), then updates status to 'Ordered' in a single action.
  const handleApproveAndSend = async (po) => {
    setActionBusy(po.id, true);
    try {
      // 1. Mark as Approved in DB
      const updated = await purchaseOrderService.approvePO(po.id);

      // 2. Trigger the Backend Email Service (JIT Dispatch)
      toast({ title: 'Approving...', description: 'Generating PDF and emailing supplier.', status: 'info', duration: 2000, position: 'top-right' });
      await purchaseOrderService.updatePOStatus(po.id, 'Ordered');
      // Assumption: A backend trigger or emailService.sendPO(po.id) would fire here

      refreshRow({ ...updated, status: 'Ordered' });
      await loadAnalytics();

      toast({ title: `${po.po_number} Approved & Emailed`, description: `Sent to ${po.supplier_name}`, status: 'success', duration: 5000, position: 'top-right' });
    } catch (err) {
      toast({ title: 'Action failed', description: poErrorMessage(err), status: 'error', position: 'top-right' }); // --- UPDATE
    } finally {
      setActionBusy(po.id, false);
      detailDisc.onClose();
    }
  };

  const openRejectDialog = (po) => {
    setRejectTarget(po);
    setRejectReason('');
    rejectDisc.onOpen();
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionBusy(rejectTarget.id, true);
    try {
      const updated = await purchaseOrderService.rejectPO(rejectTarget.id, rejectReason);
      refreshRow(updated);
      await loadAnalytics();
      toast({ title: `${rejectTarget.po_number} rejected`, status: 'warning', duration: 4000, position: 'top-right' });
    } catch (err) {
      toast({ title: 'Reject failed', description: poErrorMessage(err), status: 'error', position: 'top-right' }); // --- UPDATE: was an empty catch
    } finally {
      setActionBusy(rejectTarget.id, false);
      rejectDisc.onClose();
      setRejectTarget(null);
    }
  };

  const handleStatusChange = async (po, status) => {
    setActionBusy(po.id, true);
    try {
      await purchaseOrderService.updatePOStatus(po.id, status);
      await loadPOs();
      await loadAnalytics();
      toast({ title: `Status updated to ${status}`, status: 'success', duration: 3000, position: 'top-right' });
    } catch (err) {
      toast({ title: 'Status not changed', description: poErrorMessage(err), status: 'error', position: 'top-right' }); // --- UPDATE: was an empty catch
    } finally {
      setActionBusy(po.id, false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // DETAIL VIEW
  // ─────────────────────────────────────────────────────────
  const openDetail = async (po) => {
    // --- UPDATE: the full detail (signatures, receiving, timeline) unless legacyDetail
    if (!legacyDetail) { setDetailId(po.id); return; }
    detailDisc.onOpen();
    try {
      setSelectedPO(await purchaseOrderService.getPOById(po.id));
    } catch {}
  };

  // --- UPDATE (Procurement MOW+ v1): print, export, receive, raise from FlowSense, memo, actions
  const handlePrintPO = async (po) => {
    const w = window.open('', '_blank');
    if (!w) { toast({ title: 'Allow pop-ups to print', status: 'warning' }); return; }
    try { const full = await purchaseOrderService.getPOById(po.id); let sigs = []; try { sigs = await purchaseOrderService.getPOSignatures(po.id); } catch (_) { /* */ } w.document.write(poPrintHtml({ ...full, signatures: sigs })); w.document.close(); }
    catch (e) { w.close(); toast({ title: 'Could not print', description: poErrorMessage(e), status: 'error' }); }
  };
  const handleExportCsv = async () => {
    try { await purchaseOrderService.exportPOs(); toast({ title: 'Purchase orders exported', status: 'success', duration: 2000 }); } catch (e) { toast({ title: 'Export failed', description: poErrorMessage(e), status: 'error' }); }
  };
  const raiseFromSuggestion = (g) => {
    resetForm();
    setFormData((p) => ({ ...p, supplier_id: g.supplier_id != null ? String(g.supplier_id) : '', priority: g.items.some((i) => i.risk === 'stockout') ? 'high' : 'medium',
      notes: `--- FlowSense re-order ---\n${g.items.map((i) => `${i.quantity} × ${i.name}: ${i.reason}`).join('\n')}` }));
    setLineItems(g.items.map((i) => ({ item_id: String(i.item_id), quantity: i.quantity, quoted_price: i.unit_price ?? '' })));
    setActiveStep(g.supplier_id != null ? 1 : 0); drawerDisc.onOpen();
  };
  const runFinding = (f) => {
    const a = f.action || {};
    if (a.type === 'po' && a.po_id) setDetailId(a.po_id);
    else if (a.type === 'raise') { const g = (intel?.suggestions || []).find((x) => String(x.supplier_id) === String(a.supplier_id)) || (intel?.suggestions || [])[0]; if (g) raiseFromSuggestion(g); }
    else if (a.type === 'lead_time' && a.supplier_id) {
      purchaseOrderService.applyLeadTime(a.supplier_id, a.days).then((r) => { toast({ title: 'Lead time updated', description: `${r.lead_time_days} days — FlowSense re-order points use it now.`, status: 'success' }); loadIntel(true); })
        .catch((e) => toast({ title: 'Not updated', description: poErrorMessage(e), status: 'error' }));
    } else if (a.type === 'supplier') { setSearchTerm(f.title.split(':')[0]); }
  };
  const draftMemo = async () => {
    const primary = lineItems.find((i) => i.item_id && Number(i.quantity) > 0);
    if (!primary) { toast({ title: 'Add an item first', status: 'warning' }); return; }
    setMemoBusy(true);
    let text = null;
    try { const r = await aiService.generatePOJustification(primary.item_id, primary.quantity); if (r?.success && r.draft) text = r.draft; } catch (_) { /* offline → FlowSense memo */ }
    if (!text) {
      const supplier = suppliers.find((s) => String(s.id) === String(formData.supplier_id));
      text = localJustification({ supplierName: supplier?.name, budgetCode: formData.budget_code, priority: formData.priority, total: lineItemTotal,
        items: lineItems.filter((l) => l.item_id).map((l) => { const inv = invItems.find((x) => String(x.id) === String(l.item_id)); const ai = (invInsights || []).find((x) => String(x.id) === String(l.item_id)); return { name: inv?.name || ai?.name, quantity: l.quantity, unit_price: l.quoted_price, insight: ai }; }) });
    }
    updateField('notes', `${formData.notes ? `${formData.notes}\n\n` : ''}--- Justification ---\n${text}`);
    setMemoBusy(false);
  };
  // --- END UPDATE

  const refreshRow = (updated) => {
    setPOs(p => p.map(po => po.id === updated.id ? { ...po, ...updated } : po));
    if (selectedPO?.id === updated.id) setSelectedPO(prev => ({ ...prev, ...updated }));
  };

  // ─────────────────────────────────────────────────────────
  // ANALYTICS CARDS
  // ─────────────────────────────────────────────────────────
  const AnalyticsCards = () => (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5} mb={8}>
      {[
        {
          label: 'Total Spend (QTD)',
          // --- UPDATE: the real quarter-to-date from FlowSense (the card showed the all-time total)
          value: analyticsLoading && !intel ? '—' : intel?.totals?.spend_qtd != null ? usd(intel.totals.spend_qtd) : `$${Number(analytics?.totals?.total_spend || 0).toLocaleString()}`,
          help:  intel?.totals ? `All time ${usd(intel.totals.spend_all)} · ${analytics?.totals?.total_pos ?? intel.totals.total_pos} orders` : `${analytics?.totals?.total_pos || 0} purchase orders`,
          color: 'blue',
          icon:  MdAccountBalanceWallet,
        },
        {
          label: 'Encumbered (Pending)',
          value: analyticsLoading ? '—' : `$${Number(analytics?.totals?.encumbered || 0).toLocaleString()}`,
          help:  'Reserved funds',
          arrow: 'increase',
          color: 'orange',
          icon:  MdAttachMoney,
        },
        {
          label: 'Received Value',
          value: analyticsLoading ? '—' : `$${Number(analytics?.totals?.received_value || 0).toLocaleString()}`,
          help:  'Goods received',
          arrow: 'increase',
          color: 'green',
          icon:  MdCheckCircle,
        },
        {
          label: 'Avg Approval Time',
          value: analyticsLoading ? '—' : `${Number(analytics?.totals?.avg_approval_days || 0).toFixed(1)} days`,
          help:  'From submission',
          color: 'purple',
          icon:  MdDateRange,
        },
      ].map(card => (
        <Stat key={card.label} p={5} bg={bg} borderRadius="2xl" boxShadow="sm"
              border="1px solid" borderColor={border}>
          <HStack mb={2}>
            <Icon as={card.icon} color={`${card.color}.400`} boxSize={5} />
            <StatLabel color={secondary} fontWeight="bold" fontSize="xs" letterSpacing="wider">
              {card.label.toUpperCase()}
            </StatLabel>
          </HStack>
          {analyticsLoading
            ? <Skeleton height="32px" mt={1} />
            : <StatNumber fontSize="2xl" fontWeight="black">{card.value}</StatNumber>
          }
          <StatHelpText mt={1}>
            {card.arrow && <StatArrow type={card.arrow} />}
            {card.help}
          </StatHelpText>
        </Stat>
      ))}
    </SimpleGrid>
  );

  // ─────────────────────────────────────────────────────────
  // STATUS BREAKDOWN BAR
  // ─────────────────────────────────────────────────────────
  const StatusBreakdown = () => {
    if (!analytics?.byStatus?.length) return null;
    return (
      <Flex gap={3} mb={6} flexWrap="wrap">
        {analytics.byStatus.map(s => {
          const cfg = STATUS_CONFIG[s.status] || { color: 'gray' };
          return (
            <Tag key={s.status} colorScheme={cfg.color} borderRadius="full" px={3} py={1}
                 cursor="pointer"
                 onClick={() => { setFilterStatus(s.status); setPage(0); }}
                 variant={filterStatus === s.status ? 'solid' : 'subtle'}>
              <TagLabel fontWeight="bold">{s.status} ({s.count})</TagLabel>
            </Tag>
          );
        })}
      </Flex>
    );
  };

  // ─────────────────────────────────────────────────────────
  // TABLE ROW
  // UPDATE: Pending row now shows 'Approve & Email' (MdEmail) instead of
  // plain 'Approve' (MdThumbUp), wired to handleApproveAndSend
  // ─────────────────────────────────────────────────────────
  const PORow = ({ po, index }) => {
    const cfg = STATUS_CONFIG[po.status] || { color: 'gray', icon: MdReceipt, label: po.status };
    const busy = actionLoading[po.id];
    return (
      <MotionTr
        key={po.id}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.04 }}
        _hover={{ bg: hoverBg, cursor: 'pointer' }}
        onClick={() => openDetail(po)}
      >
        <Td>
          <VStack align="start" spacing={0}>
            <Text fontWeight="black" color="blue.500" fontSize="sm">
              {po.po_number || `#${po.id}`}
            </Text>
            {/* --- UPDATE: late delivery */}
            {overdueIds.get(String(po.id)) && <Tag size="sm" colorScheme="red" borderRadius="full"><TagLeftIcon as={MdWarning} /><TagLabel>{overdueIds.get(String(po.id)).days_late} d late</TagLabel></Tag>}
            <Text fontSize="xs" color={secondary}>
              {po.order_date ? new Date(po.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
            </Text>
          </VStack>
        </Td>
        <Td>
          <VStack align="start" spacing={0}>
            <HStack spacing={1}><Text fontWeight="semibold" fontSize="sm">{po.supplier_name || '—'}</Text>
              {supplierScore.get(String(po.supplier_id))?.supplier_grade && <Tooltip hasArrow label={supplierScore.get(String(po.supplier_id)).grade_reason}><Badge colorScheme={gradeMeta(supplierScore.get(String(po.supplier_id)).supplier_grade).color} fontSize="2xs">{supplierScore.get(String(po.supplier_id)).supplier_grade}</Badge></Tooltip>}</HStack>{/* --- UPDATE: supplier grade */}
            {po.supplier_contact && <Text fontSize="xs" color={secondary}>{po.supplier_contact}</Text>}
          </VStack>
        </Td>
        <Td>
          {po.budget_code
            ? <Tag size="sm" colorScheme="purple" borderRadius="full"><TagLabel>{po.budget_code}</TagLabel></Tag>
            : <Text color={secondary} fontSize="xs">—</Text>}
        </Td>
        <Td>
          <Tag size="sm" colorScheme={{ low: 'gray', medium: 'blue', high: 'orange', critical: 'red' }[po.priority] || 'gray'}>
            {po.priority}
          </Tag>
        </Td>
        <Td isNumeric>
          <Text fontWeight="black" fontSize="sm">${Number(po.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
        </Td>
        <Td>
          <Tag colorScheme={cfg.color} borderRadius="full" size="sm">
            <TagLeftIcon as={cfg.icon} />
            <TagLabel>{cfg.label}</TagLabel>
          </Tag>
        </Td>
        <Td onClick={e => e.stopPropagation()}>
          {busy
            ? <Spinner size="xs" />
            : (
              <Menu>
                <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" aria-label={`Actions ${po.po_number || po.id}`} />{/* --- UPDATE: named */}
                <MenuList fontSize="sm" shadow="lg">
                  <MenuItem icon={<Icon as={MdOpenInNew} />} onClick={() => openDetail(po)}>View Details</MenuItem>
                  {po.status === 'Pending' && (
                    <>
                      {/* UPDATE: Approve & Email replaces plain Approve */}
                      {canOverride && <MenuItem icon={<Icon as={MdEmail} color="blue.500" />} onClick={() => handleApproveAndSend(po)}>Approve & Email</MenuItem>}{/* --- UPDATE: managers only (the server checks too) */}
                      <MenuItem icon={<Icon as={MdThumbDown} color="red.500" />} onClick={() => openRejectDialog(po)}>Reject</MenuItem>
                    </>
                  )}
                  {po.status === 'Ordered' && (
                    <MenuItem onClick={() => handleStatusChange(po, 'Shipped')}>Mark as Shipped</MenuItem>
                  )}
                  {po.status === 'Shipped' && (
                    <MenuItem onClick={() => handleStatusChange(po, 'Received')}>Mark as Received</MenuItem>
                  )}
                  {/* --- UPDATE (Procurement MOW+ v1) */}
                  {['Ordered', 'Shipped', 'Partial'].includes(po.status) && canRaise && (
                    <MenuItem icon={<Icon as={MdInventory} color="green.500" />} onClick={() => setReceiving(po)}>Receive goods…</MenuItem>
                  )}
                  <MenuItem icon={<Icon as={MdPrint} />} onClick={() => handlePrintPO(po)}>Print / PDF</MenuItem>
                  {['Pending', 'Ordered'].includes(po.status) && (String(po.created_by_user_id) === String(me.id) || canOverride) && (
                    <MenuItem icon={<Icon as={MdCancel} color="red.400" />} onClick={() => { if (window.confirm(`Cancel ${po.po_number || `PO #${po.id}`}?`)) handleStatusChange(po, 'Cancelled'); }}>Cancel order</MenuItem>
                  )}
                  {/* --- END UPDATE */}
                </MenuList>
              </Menu>
            )
          }
        </Td>
      </MotionTr>
    );
  };

  // ─────────────────────────────────────────────────────────
  // LINE ITEM EDITOR (wizard step 1)
  // ─────────────────────────────────────────────────────────
  const LineItemEditor = () => (
    <Box>
      <HStack mb={3} justify="space-between">
        <Text fontWeight="bold" fontSize="sm">Line Items</Text>
        <Button size="xs" leftIcon={<MdAdd />} colorScheme="blue" variant="outline" onClick={addLineItem}>
          Add Item
        </Button>
      </HStack>
      <InputGroup mb={3} size="sm">
        <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
        <Input placeholder="Search catalogue items…" value={invSearch}
               onChange={e => setInvSearch(e.target.value)} borderRadius="lg" />
      </InputGroup>
      <VStack spacing={3}>
        {lineItems.map((li, idx) => {
          const selItem = invItems.find(i => String(i.id) === String(li.item_id));
          return (
            <Box key={idx} p={3} border="1px solid" borderColor={border}
                 borderRadius="xl" w="full" bg={subtleBg}>
              <HStack mb={2} justify="space-between">
                <Text fontSize="xs" fontWeight="bold" color={secondary}>ITEM {idx + 1}</Text>
                {lineItems.length > 1 && (
                  <IconButton icon={<MdClose />} size="xs" variant="ghost"
                              colorScheme="red" aria-label="Remove"
                              onClick={() => removeLineItem(idx)} />
                )}
              </HStack>
              <SimpleGrid columns={3} spacing={2}>
                <FormControl gridColumn="1 / -1" isInvalid={!!formErrors[`item_${idx}_id`]}>
                  <Select size="sm" borderRadius="lg" value={li.item_id}
                          placeholder="— Select inventory item —"
                          onChange={e => updateLineItem(idx, 'item_id', e.target.value)}>
                    {invItems.map(inv => (
                      <option key={inv.id} value={inv.id}>
                        {inv.name} {inv.quantity <= inv.low_stock_threshold ? '⚠ Low Stock' : ''}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage fontSize="xs">{formErrors[`item_${idx}_id`]}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!formErrors[`item_${idx}_qty`]}>
                  <FormLabel fontSize="xs">Qty</FormLabel>
                  <NumberInput size="sm" min={1} value={li.quantity}
                               onChange={v => updateLineItem(idx, 'quantity', v)}>
                    <NumberInputField borderRadius="lg" />
                    <NumberInputStepper>
                      <NumberIncrementStepper /><NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage fontSize="xs">{formErrors[`item_${idx}_qty`]}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!formErrors[`item_${idx}_price`]}>
                  <FormLabel fontSize="xs">Unit Price ($)</FormLabel>
                  <NumberInput size="sm" min={0} precision={2} value={li.quoted_price}
                               onChange={v => updateLineItem(idx, 'quoted_price', v)}>
                    <NumberInputField borderRadius="lg" />
                  </NumberInput>
                  <FormErrorMessage fontSize="xs">{formErrors[`item_${idx}_price`]}</FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Subtotal</FormLabel>
                  <Text fontWeight="black" fontSize="sm" pt={2}>
                    ${((parseFloat(li.quoted_price) || 0) * (parseInt(li.quantity, 10) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                </FormControl>
              </SimpleGrid>
              {/* --- UPDATE (Procurement MOW+ v1): FlowSense check of this line */}
              {li.item_id && checkByItem.get(String(li.item_id)) && (() => {
                const c = checkByItem.get(String(li.item_id));
                const warns = (check?.warnings || []).filter((w) => String(w.item_id) === String(li.item_id));
                return (
                  <Box mt={2} p={2} bg={tile} borderRadius="lg" fontSize="xs" data-testid={`po-line-check-${li.item_id}`}>
                    <HStack spacing={2} flexWrap="wrap"><Icon as={MdAutoAwesome} color="teal.400" />
                      <Text>{c.on_hand ?? '—'} in stock</Text><Text>· {c.on_order} on order</Text>
                      {c.suggested !== null && <Text>· FlowSense suggests {c.suggested}</Text>}
                      {c.cover_after_days !== null && <Text>· ≈ {c.cover_after_days} d cover after delivery</Text>}
                      {c.reference_price !== null && <Text>· paid before {usd(c.reference_price)}</Text>}
                      {c.suggested > 0 && Number(li.quantity) !== c.suggested && <Button size="xs" variant="link" colorScheme="teal" onClick={() => updateLineItem(idx, 'quantity', c.suggested)}>use {c.suggested}</Button>}
                    </HStack>
                    {warns.map((w, i) => <Text key={i} color={w.level === 'error' ? 'red.500' : w.level === 'warning' ? 'orange.500' : 'blue.500'} mt={0.5}>⚠ {w.text}</Text>)}
                  </Box>
                );
              })()}
              {/* --- END UPDATE */}
            </Box>
          );
        })}
      </VStack>
      <Box mt={4} p={3} bg={totalBg} borderRadius="xl" border="1px solid" borderColor="blue.100">{/* --- UPDATE: dark mode */}
        <Flex justify="space-between">
          <Text fontWeight="bold" color={totalText}>Estimated Total</Text>
          <Text fontWeight="black" fontSize="lg" color={totalText}>
            ${lineItemTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </Text>
        </Flex>
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────────────────
  // WIZARD STEP CONTENT
  // ─────────────────────────────────────────────────────────
  const WizardStep = () => {
    switch (activeStep) {
      case 0: return (
        <VStack spacing={5}>
          <FormControl isRequired isInvalid={!!formErrors.supplier_id}>
            <FormLabel fontWeight="bold">Supplier</FormLabel>
            <Select value={formData.supplier_id} borderRadius="xl"
                    placeholder="— Select supplier —"
                    onChange={e => updateField('supplier_id', e.target.value)}>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.contact_person ? `(${s.contact_person})` : ''}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{formErrors.supplier_id}</FormErrorMessage>
          </FormControl>
          {/* --- UPDATE (Procurement MOW+ v1): supplier record + delivery date from the real lead time */}
          {check?.supplier && (
            <Box w="full" p={3} bg={tile} borderRadius="xl" borderWidth="1px" borderColor={border} fontSize="sm">
              <HStack spacing={2} flexWrap="wrap"><Icon as={MdAutoAwesome} color="teal.400" /><Badge colorScheme={gradeMeta(check.supplier.supplier_grade).color}>{check.supplier.supplier_grade || 'unrated'}</Badge>
                <Text>{check.supplier.grade_reason}</Text></HStack>
              {check.lead_time_days !== null && (
                <HStack mt={1} spacing={2}><Text>Lead time {check.lead_time_days} days ({check.lead_time_source}) → expected {check.suggested_delivery_date}</Text>
                  <Button size="xs" variant="outline" colorScheme="teal" onClick={() => updateField('expected_delivery_date', check.suggested_delivery_date)}>Use this date</Button></HStack>
              )}
            </Box>
          )}
          {/* --- END UPDATE */}
          <SimpleGrid columns={2} spacing={4} w="full">
            <FormControl isRequired isInvalid={!!formErrors.order_date}>
              <FormLabel fontWeight="bold">Order Date</FormLabel>
              <Input type="date" borderRadius="xl" value={formData.order_date}
                     onChange={e => updateField('order_date', e.target.value)} />
              <FormErrorMessage>{formErrors.order_date}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="bold">Expected Delivery</FormLabel>
              <Input type="date" borderRadius="xl" value={formData.expected_delivery_date}
                     onChange={e => updateField('expected_delivery_date', e.target.value)} />
            </FormControl>
          </SimpleGrid>
          <FormControl>
            <FormLabel fontWeight="bold">Priority</FormLabel>
            <Select value={formData.priority} borderRadius="xl"
                    onChange={e => updateField('priority', e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
          </FormControl>
        </VStack>
      );

      case 1: return LineItemEditor(); // --- UPDATE: called, not mounted (was <LineItemEditor /> — remounted on every keystroke)

      case 2: return (
        <VStack spacing={5}>
          <FormControl isRequired isInvalid={!!formErrors.budget_code}>
            <FormLabel fontWeight="bold">Budget Code / Vote</FormLabel>
            <Input value={formData.budget_code} borderRadius="xl"
                   placeholder="e.g. MAINT-204"
                   list="po-budget-codes" // --- UPDATE: budget votes used before
                   onChange={e => updateField('budget_code', e.target.value)} />
            <datalist id="po-budget-codes">{(check?.budget_codes || (intel?.budget || []).map((b) => b.budget_code)).filter((b) => b && b !== '(none)').map((b) => <option key={b} value={b} />)}</datalist>
            <FormErrorMessage>{formErrors.budget_code}</FormErrorMessage>
          </FormControl>
          <FormControl>
            {/* --- UPDATE: FlowSense memo */}
            <HStack justify="space-between" mb={2}>
            <FormLabel fontWeight="bold" mb={0}>Notes / Justification</FormLabel>
              <Button size="xs" colorScheme="purple" variant="outline" leftIcon={<Icon as={MdAutoAwesome} />} onClick={draftMemo} isLoading={memoBusy} borderRadius="full">FlowSense memo</Button>
            </HStack>
            <Textarea value={formData.notes} borderRadius="xl" rows={5}
                      placeholder="Detailed justification for this purchase…"
                      onChange={e => updateField('notes', e.target.value)} />
          </FormControl>
        </VStack>
      );

      case 3: {
        const supplier = suppliers.find(s => String(s.id) === String(formData.supplier_id));
        return (
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold" fontSize="md">Review & Confirm</Text>
            <SimpleGrid columns={2} spacing={3} fontSize="sm">
              <Box><Text color={secondary}>Supplier</Text><Text fontWeight="bold">{supplier?.name || '—'}</Text></Box>
              <Box><Text color={secondary}>Order Date</Text><Text fontWeight="bold">{formData.order_date}</Text></Box>
              <Box><Text color={secondary}>Budget Code</Text><Text fontWeight="bold">{formData.budget_code || '—'}</Text></Box>
              <Box><Text color={secondary}>Priority</Text>
                <Tag size="sm" colorScheme={{ low:'gray',medium:'blue',high:'orange',critical:'red' }[formData.priority]}>{formData.priority}</Tag>
              </Box>
            </SimpleGrid>
            <Divider />
            <Text fontWeight="bold" fontSize="sm">Line Items ({lineItems.length})</Text>
            {lineItems.map((li, i) => {
              const inv = invItems.find(x => String(x.id) === String(li.item_id));
              return (
                <Flex key={i} justify="space-between" fontSize="sm" p={2} bg={subtleBg} borderRadius="lg">
                  <Text>{inv?.name || `Item #${li.item_id}`} × {li.quantity}</Text>
                  <Text fontWeight="bold">${((parseFloat(li.quoted_price)||0)*(parseInt(li.quantity,10)||0)).toLocaleString(undefined,{minimumFractionDigits:2})}</Text>
                </Flex>
              );
            })}
            <Box p={4} bg="blue.50" borderRadius="xl" border="1px solid" borderColor="blue.100">
              <Flex justify="space-between">
                <Text fontWeight="bold" color="blue.700">Total to be Encumbered</Text>
                <Text fontWeight="black" fontSize="xl" color="blue.700">
                  ${lineItemTotal.toLocaleString(undefined,{minimumFractionDigits:2})}
                </Text>
              </Flex>
              <Text fontSize="xs" color="blue.600" mt={1}>
                Against budget code <strong>{formData.budget_code}</strong>. Pending approval.
              </Text>
            </Box>
            {/* --- UPDATE: FlowSense warnings before submitting */}
            {(check?.warnings || []).length > 0 && (
              <Box p={3} bg={warnBg} borderRadius="xl" fontSize="sm" role="note" aria-label="FlowSense checks">
                <HStack mb={1}><Icon as={MdAutoAwesome} color="orange.400" /><Text fontWeight="bold">FlowSense checks</Text></HStack>
                {check.warnings.map((w, i) => <Text key={i}>• {w.text}</Text>)}
              </Box>
            )}
          </VStack>
        );
      }

      default: return null;
    }
  };

  // ─────────────────────────────────────────────────────────
  // PO DETAIL MODAL
  // UPDATE: Footer now shows 'Approve & Email' (MdEmail) calling
  // handleApproveAndSend instead of plain 'Approve' (MdThumbUp)
  // ─────────────────────────────────────────────────────────
  const PODetailModal = () => (
    <Modal isOpen={detailDisc.isOpen} onClose={() => { detailDisc.onClose(); setSelectedPO(null); }}
           size="2xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>
          {selectedPO
            ? <HStack>
                <Icon as={MdReceipt} color="blue.500" />
                <Text>{selectedPO.po_number}</Text>
                <Tag colorScheme={(STATUS_CONFIG[selectedPO.status]||{}).color||'gray'} size="sm">
                  {selectedPO.status}
                </Tag>
              </HStack>
            : <Skeleton height="24px" width="200px" />
          }
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {!selectedPO
            ? <SkeletonText noOfLines={8} spacing={4} />
            : (
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={2} spacing={4} fontSize="sm">
                  <Box><Text color={secondary}>Supplier</Text><Text fontWeight="bold">{selectedPO.supplier_name}</Text></Box>
                  <Box><Text color={secondary}>Order Date</Text><Text fontWeight="bold">{new Date(selectedPO.order_date).toLocaleDateString()}</Text></Box>
                  <Box><Text color={secondary}>Budget Code</Text><Text fontWeight="bold">{selectedPO.budget_code || '—'}</Text></Box>
                  <Box><Text color={secondary}>Created by</Text><Text fontWeight="bold">{selectedPO.created_by_username || '—'}</Text></Box>
                </SimpleGrid>

                <Divider />
                <Text fontWeight="bold">Line Items</Text>
                <Table size="sm" variant="simple">
                  <Thead bg={headerBg}><Tr>
                    <Th>Item</Th><Th isNumeric>Qty</Th>
                    <Th isNumeric>Unit Price</Th><Th isNumeric>Subtotal</Th>
                  </Tr></Thead>
                  <Tbody>
                    {(selectedPO.items || []).map(item => (
                      <Tr key={item.id}>
                        <Td><Text fontWeight="semibold">{item.item_name}</Text></Td>
                        <Td isNumeric>{item.quantity}</Td>
                        <Td isNumeric>${Number(item.quoted_price).toLocaleString(undefined,{minimumFractionDigits:2})}</Td>
                        <Td isNumeric fontWeight="bold">${(item.quantity * item.quoted_price).toLocaleString(undefined,{minimumFractionDigits:2})}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Flex justify="flex-end">
                  <Tag colorScheme="blue" size="lg" px={4}>
                    Total: <strong style={{ marginLeft: 8 }}>${Number(selectedPO.total_amount).toLocaleString(undefined,{minimumFractionDigits:2})}</strong>
                  </Tag>
                </Flex>

                {selectedPO.notes && (
                  <Box p={4} bg={subtleBg} borderRadius="xl" fontSize="sm">
                    <Text fontWeight="bold" mb={1}>Notes</Text>
                    <Text whiteSpace="pre-wrap">{selectedPO.notes}</Text>
                  </Box>
                )}
              </VStack>
            )
          }
        </ModalBody>
        <ModalFooter gap={3}>
          {selectedPO?.status === 'Pending' && (
            <>
              {/* UPDATE: Approve & Email replaces plain Approve in the detail modal */}
              <Button colorScheme="blue" leftIcon={<MdEmail />}
                      isLoading={actionLoading[selectedPO?.id]}
                      onClick={() => { handleApproveAndSend(selectedPO); }}>
                Approve & Email
              </Button>
              <Button colorScheme="red" variant="outline" leftIcon={<MdThumbDown />}
                      onClick={() => { openRejectDialog(selectedPO); detailDisc.onClose(); }}>
                Reject
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={() => { detailDisc.onClose(); setSelectedPO(null); }}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <Box p={{ base: 3, md: 6 }}>

      {/* HEADER */}
      <Flex justify="space-between" align="center" mb={8} flexWrap="wrap" gap={4}>
        <VStack align="start" spacing={0}>
          <HStack>
            <Icon as={MdAccountBalanceWallet} color="blue.500" />
            <Text fontWeight="bold" fontSize="xs" letterSpacing="widest" color="blue.500">FINANCIAL CONTROL</Text>
          </HStack>
          <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="black" letterSpacing="tight">
            Purchase Orders & Budgeting
          </Text>
        </VStack>
        <HStack>
          <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} />{/* --- UPDATE */}
          <Tooltip label="Refresh">
            <IconButton icon={<MdRefresh />} variant="outline" borderRadius="xl"
                        aria-label="Refresh" onClick={() => { loadPOs(); loadAnalytics(); loadIntel(true); }} />
          </Tooltip>
          <Tooltip label="Export CSV"><IconButton icon={<MdDownload />} variant="outline" borderRadius="xl" aria-label="Export purchase orders" onClick={handleExportCsv} /></Tooltip>{/* --- UPDATE */}
          {canRaise && ( /* --- UPDATE: office roles */
          <Button leftIcon={<MdAdd />} colorScheme="blue" size="lg" borderRadius="xl"
                  onClick={() => { resetForm(); drawerDisc.onOpen(); }}>
            Raise New PO
          </Button>
          )}
        </HStack>
      </Flex>

      {/* --- UPDATE (Procurement MOW+ v1): FlowSense procurement AI */}
      <Box mb={6}>
        <FlowSenseInsightBar screen="procurement" title="Purchase Orders" data={intel}
          headline={intel ? [`Health ${intel.score}/100 · ${intel.totals.open_pos} open (${usd(intel.totals.committed_value)}) · ${intel.totals.pending} awaiting approval · ${intel.totals.overdue} late`, (intel.findings || [])[0] ? `Top finding: ${intel.findings[0].title}` : 'No findings — purchasing looks healthy.'] : intelError || 'Loading FlowSense purchasing intelligence…'}
          rulesAnswer={(q) => answerProcurementLocally(q, intel).answer} ministryText={explainProcurementForMinistry}
          contextBuilder={() => buildProcurementContext(intel)} questions={PROCUREMENT_QUESTIONS} />
      </Box>
      {intel && ((intel.findings || []).length > 0 || (intel.suggestions || []).length > 0) && (
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5} mb={6}>
          <Box bg={bg} p={4} borderRadius="2xl" border="1px solid" borderColor={border} role="region" aria-label="FlowSense purchasing findings">
            <HStack mb={2}><Icon as={MdAutoAwesome} color="teal.400" /><Text fontWeight="800">FlowSense findings</Text><Badge>{intel.findings.length}</Badge></HStack>
            <VStack align="stretch" spacing={2}>
              {(showAllFindings ? intel.findings : intel.findings.slice(0, 5)).map((f) => (
                <Flex key={f.id} p={2} bg={subtleBg} borderRadius="lg" borderLeftWidth="4px" borderLeftColor={`${LEVEL_COLOR[f.level] || 'gray'}.400`} gap={2} align="center">
                  <Box flex={1} minW={0}><HStack spacing={2}><Badge colorScheme={LEVEL_COLOR[f.level]} fontSize="2xs">{f.level}</Badge><Text fontSize="sm" fontWeight="700" noOfLines={1}>{f.title}</Text>{f.ml && <Badge colorScheme="purple" fontSize="2xs">ML</Badge>}</HStack>
                    <Text fontSize="xs" color={secondary} noOfLines={2}>{f.detail}</Text></Box>
                  {f.action && <Button size="xs" rightIcon={<MdArrowForward />} onClick={() => runFinding(f)} isDisabled={(f.action.type === 'raise' && !canRaise) || (f.action.type === 'lead_time' && !canOverride)}>
                    {f.action.type === 'po' ? 'Open' : f.action.type === 'raise' ? 'Raise PO' : f.action.type === 'lead_time' ? `Use ${f.action.days} d` : 'Show'}</Button>}
                </Flex>
              ))}
              {intel.findings.length > 5 && <Button size="xs" variant="ghost" onClick={() => setShowAllFindings((x) => !x)}>{showAllFindings ? 'Show fewer' : `Show all ${intel.findings.length}`}</Button>}
              {!intel.findings.length && <Text fontSize="sm" color={secondary}>No findings.</Text>}
            </VStack>
          </Box>
          <Box bg={bg} p={4} borderRadius="2xl" border="1px solid" borderColor={border} role="region" aria-label="FlowSense what to order">
            <HStack mb={2}><Icon as={MdAutoAwesome} color="teal.400" /><Text fontWeight="800">What to order (FlowSense)</Text>{intel.totals.forecast_30d_spend ? <Badge colorScheme="teal">{usd(intel.totals.forecast_30d_spend)}</Badge> : null}</HStack>
            <Text fontSize="xs" color={secondary} mb={2}>Inventory forecast (usage, lead time, safety stock) minus what is already on order, by supplier.</Text>
            <VStack align="stretch" spacing={2}>
              {(intel.suggestions || []).slice(0, 6).map((g) => (
                <Flex key={String(g.supplier_id)} p={2} bg={subtleBg} borderRadius="lg" gap={2} align="center">
                  <Box flex={1} minW={0}><Text fontSize="sm" fontWeight="700">{g.supplier_name || 'No supplier set'} {g.total ? `· ${usd(g.total)}` : ''}</Text>
                    <Text fontSize="xs" color={secondary} noOfLines={2}>{g.items.map((i) => `${i.quantity} × ${i.name}${i.risk === 'stockout' ? ' (out)' : ''}`).join(', ')}</Text></Box>
                  {canRaise && <Button size="xs" colorScheme="blue" onClick={() => raiseFromSuggestion(g)} aria-label={`Raise PO for ${g.supplier_name || 'unassigned items'}`}>Raise PO</Button>}
                </Flex>
              ))}
              {!(intel.suggestions || []).length && <Text fontSize="sm" color={secondary}>Nothing to order — stock plus what is on order covers the forecast.</Text>}
            </VStack>
          </Box>
        </SimpleGrid>
      )}
      {/* --- END UPDATE */}

      {/* ANALYTICS */}
      {AnalyticsCards()}{/* --- UPDATE: called, not mounted */}
      {StatusBreakdown()}

      {/* SEARCH + FILTER BAR */}
      <Flex mb={5} gap={3} bg={bg} p={4} borderRadius="2xl" border="1px solid" borderColor={border}
            flexWrap="wrap">
        <InputGroup flex="1" minW="200px">
          <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
          <Input placeholder="Search PO#, Vendor, Budget Code…" variant="unstyled" pl={10}
                 value={searchTerm} onChange={e => setSearchTerm(e.target.value)} aria-label="Search purchase orders" />
        </InputGroup>
        <Select variant="unstyled" w={{ base: 'full', sm: '180px' }} fontWeight="bold" aria-label="PO status filter"
                value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}>
          <option value="All">All Statuses</option>
          {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s === 'Partial' ? 'Partly received' : s}</option>)}
        </Select>
      </Flex>

      {/* TABLE */}
      <Box bg={bg} borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor={border}>
        {loading ? (
          <Flex direction="column" align="center" justify="center" p={20}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text mt={4} color={secondary} fontWeight="bold">Loading Purchase Orders…</Text>
          </Flex>
        ) : pos.length === 0 ? (
          <Flex direction="column" align="center" justify="center" p={20} gap={3}>
            <Icon as={MdReceipt} boxSize={16} color="gray.300" />
            <Text color={secondary} fontWeight="bold">No purchase orders found</Text>
            <Button size="sm" leftIcon={<MdAdd />} colorScheme="blue" variant="outline"
                    onClick={() => { resetForm(); drawerDisc.onOpen(); }}>
              Create First PO
            </Button>
          </Flex>
        ) : (
          <>
            <Table variant="simple">
              <Thead bg={headerBg}>
                <Tr>
                  <Th py={4}>PO Details</Th>
                  <Th>Supplier</Th>
                  <Th>Budget Code</Th>
                  <Th>Priority</Th>
                  <Th isNumeric>Amount</Th>
                  <Th>Status</Th>
                  <Th w="50px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                <AnimatePresence>
                  {pos.map((po, idx) => PORow({ po, index: idx }))}{/* --- UPDATE: called, not mounted (menus closed on every refresh) */}
                </AnimatePresence>
              </Tbody>
            </Table>

            {/* PAGINATION */}
            {total > LIMIT && (
              <Flex justify="space-between" align="center" p={4} borderTop="1px solid" borderColor={border}>
                <Text fontSize="sm" color={secondary}>
                  Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
                </Text>
                <HStack>
                  <Button size="sm" variant="outline" isDisabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <Button size="sm" variant="outline" isDisabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                </HStack>
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* ─── NEW PO DRAWER ──────────────────────────────────── */}
      <Drawer isOpen={drawerDisc.isOpen} placement="right" onClose={drawerDisc.onClose} size="lg">
        <DrawerOverlay backdropFilter="blur(6px)" />
        <DrawerContent bg={bg}>
          <DrawerHeader borderBottomWidth="1px">
            <HStack>
              <Icon as={MdReceipt} color="blue.500" />
              <Text>Raise New Purchase Order</Text>
            </HStack>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody p={6} overflowY="auto">
            <Stepper index={activeStep} mb={8} colorScheme="blue" size="sm">
              {WIZARD_STEPS.map((step, i) => (
                <Step key={i}>
                  <StepIndicator>
                    <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                  </StepIndicator>
                  <Box flexShrink="0"><StepTitle>{step.title}</StepTitle></Box>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>

            {WizardStep()}{/* --- UPDATE: called, not mounted (inputs lost focus on every keystroke) */}
          </DrawerBody>

          <Flex p={5} borderTopWidth="1px" justify="space-between" gap={3}>
            <Button variant="ghost" onClick={activeStep === 0 ? drawerDisc.onClose : handleBack}>
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            {activeStep < WIZARD_STEPS.length - 1 ? (
              <Button colorScheme="blue" onClick={handleNext}>Next</Button>
            ) : (
              <Button colorScheme="blue" isLoading={submitting} loadingText="Submitting…" onClick={handleSubmitPO} px={8}>
                Submit for Approval
              </Button>
            )}
          </Flex>
        </DrawerContent>
      </Drawer>

      {/* ─── PO DETAIL MODAL ────────────────────────────────── */}
      {legacyDetail ? PODetailModal() : ( /* --- UPDATE: the full detail (signatures, receiving, timeline, AI) */
        <PurchaseOrderDetailModal isOpen={!!detailId} poId={detailId} onClose={() => setDetailId(null)} intel={intel} insights={invInsights}
          onStatusUpdate={() => { loadPOs(true); loadAnalytics(); loadIntel(true); }} />
      )}
      {/* --- UPDATE: goods receipt from the list */}
      <ReceiveGoodsModal isOpen={!!receiving} onClose={() => setReceiving(null)} po={receiving} insights={invInsights}
        onDone={(r) => { toast({ title: r.complete ? 'Order received' : 'Goods received', description: r.received.map((x) => `${x.quantity} × ${x.item_name}`).join(', '), status: 'success', duration: 4000, position: 'top-right' }); loadPOs(true); loadAnalytics(); loadIntel(true); loadInsights(); }} />

      {/* ─── REJECT CONFIRM DIALOG ──────────────────────────── */}
      <AlertDialog isOpen={rejectDisc.isOpen} leastDestructiveRef={cancelRef} onClose={rejectDisc.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader>Reject Purchase Order</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>Rejecting <strong>{rejectTarget?.po_number}</strong>. Please provide a reason (optional):</Text>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                        placeholder="Rejection reason…" borderRadius="xl" rows={3} />
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} onClick={rejectDisc.onClose}>Cancel</Button>
              <Button colorScheme="red" isLoading={actionLoading[rejectTarget?.id]} onClick={handleReject}>
                Confirm Reject
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

    </Box>
  );
};

export default PurchaseOrderManager;